import React, { useEffect, useState } from 'react';
import { getMapData } from '../api/client';
import { MapPin, User, Briefcase, RefreshCw } from 'lucide-react';

export default function MapView() {
  const [data, setData] = useState({ technicians: [], jobs: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  const load = () => {
    setLoading(true);
    getMapData().then(r => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  return (
    <div style={{ height: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>GPS Tracking</h1>
        <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fff', border: '1px solid #D1D5DB', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
        {/* Sidebar */}
        <div style={{ width: 260, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
          {/* Technicians */}
          <div style={{ background: '#fff', borderRadius: 10, padding: 14, border: '1px solid #E5E7EB' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <User size={14} /> Technicians ({data.technicians.length})
            </h3>
            {data.technicians.length === 0 ? (
              <p style={{ fontSize: 12, color: '#9CA3AF' }}>No technicians sharing location</p>
            ) : data.technicians.map(t => (
              <div key={t.id} onClick={() => setSelected(t)} style={{
                padding: '8px 10px', borderRadius: 7, marginBottom: 6, cursor: 'pointer',
                background: selected?.id === t.id ? '#EFF6FF' : '#F9FAFB',
                border: selected?.id === t.id ? '1px solid #BFDBFE' : '1px solid transparent',
              }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: t.is_available ? '#10B981' : '#EF4444' }} />
                  <span style={{ fontSize: 11, color: '#6B7280' }}>{t.is_available ? 'Available' : 'Busy'}</span>
                </div>
                {t.last_update && <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>Updated: {new Date(t.last_update).toLocaleTimeString()}</div>}
              </div>
            ))}
          </div>

          {/* Active Jobs */}
          <div style={{ background: '#fff', borderRadius: 10, padding: 14, border: '1px solid #E5E7EB' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Briefcase size={14} /> Active Jobs ({data.jobs.length})
            </h3>
            {data.jobs.length === 0 ? (
              <p style={{ fontSize: 12, color: '#9CA3AF' }}>No active jobs with coordinates</p>
            ) : data.jobs.map(j => (
              <div key={j.id} style={{ padding: '8px 10px', borderRadius: 7, marginBottom: 6, background: '#F9FAFB' }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: '#2563EB' }}>{j.job_number}</div>
                <div style={{ fontSize: 12, color: '#374151' }}>{j.customer_name}</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{j.address}</div>
                <span style={{
                  display: 'inline-block', marginTop: 4, padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                  background: j.priority === 'urgent' ? '#FEE2E2' : '#EFF6FF',
                  color: j.priority === 'urgent' ? '#DC2626' : '#2563EB',
                }}>
                  {j.priority} · {j.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Map */}
        <div style={{ flex: 1, borderRadius: 12, overflow: 'hidden', border: '1px solid #E5E7EB', background: '#F0F4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {GMAPS_KEY ? (
            <iframe
              title="GPS Map"
              width="100%" height="100%" frameBorder="0" style={{ border: 0 }}
              src={`https://www.google.com/maps/embed/v1/view?key=${GMAPS_KEY}&center=34.0522,-118.2437&zoom=11`}
              allowFullScreen
            />
          ) : (
            <div style={{ textAlign: 'center', color: '#6B7280' }}>
              <MapPin size={48} color="#D1D5DB" style={{ margin: '0 auto 16px' }} />
              <p style={{ fontWeight: 600, fontSize: 16, color: '#374151', marginBottom: 8 }}>Map requires Google Maps API Key</p>
              <p style={{ fontSize: 13 }}>Add <code style={{ background: '#F3F4F6', padding: '2px 6px', borderRadius: 4 }}>REACT_APP_GOOGLE_MAPS_KEY</code> to your <code style={{ background: '#F3F4F6', padding: '2px 6px', borderRadius: 4 }}>.env</code> file</p>
              <div style={{ marginTop: 24, padding: 16, background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', textAlign: 'left', maxWidth: 400 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Technician locations from API:</p>
                {data.technicians.map(t => (
                  <div key={t.id} style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                    {t.name}: {t.lat ? `${t.lat.toFixed(4)}, ${t.lng.toFixed(4)}` : 'No location data'}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
